import React, { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowsExpandIcon, MinusIcon, PencilAltIcon, XIcon } from '@heroicons/react/outline';
import Button from './Button';
import BackButton from './BackButton';
import { cn } from './cn';

/** Nonmodal composition surface. A stable portal preserves editors when another dialog hosts it. */
export default function ComposeWorkspace({ portalTarget, fullscreen, hasActive, dock, notice, children }) {
  const [host] = useState(() => document.createElement('div'));
  const [left, setLeft] = useState(0);
  useLayoutEffect(() => {
    (portalTarget || document.body).appendChild(host);
    return () => host.remove();
  }, [host, portalTarget]);
  useEffect(() => {
    if (!fullscreen || !hasActive) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [fullscreen, hasActive]);
  useLayoutEffect(() => {
    const update = () => {
      const sidebar = document.querySelector('[data-bun-sidebar]');
      setLeft(Math.max(0, sidebar?.getBoundingClientRect().right || 0));
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', update);
    return () => { observer.disconnect(); window.removeEventListener('resize', update); };
  }, []);
  return createPortal(<div className="bun font-instrument text-bun-text">
    <div data-testid="email-compose-layer" className="pointer-events-none fixed inset-y-0 right-0 z-[110]" style={{ left }}>
      <div className={cn('absolute', hasActive && 'pointer-events-auto', fullscreen && hasActive ? 'inset-0' : 'bottom-0 right-3 w-[600px] max-w-[calc(100%-24px)] h-[min(680px,calc(100dvh-24px))] sm:right-6 sm:max-w-[calc(100%-48px)]')}>
        {children}
      </div>
      {!fullscreen && <div className={cn('pointer-events-auto absolute bottom-0 left-3 justify-end gap-3 overflow-x-auto sm:left-6', hasActive ? 'hidden sm:flex sm:right-[648px]' : 'right-3 flex sm:right-6')}>{dock}</div>}
      {notice && <div className="pointer-events-auto absolute bottom-16 left-3 right-3 sm:right-auto sm:max-w-lg">{notice}</div>}
    </div>
  </div>, host);
}

function Panel({ active, fullscreen, title, status, onMinimize, onToggleFullscreen, onClose, otherDrafts, footer, children }) {
  const panelRef = React.useRef(null);
  useEffect(() => {
    if (active) panelRef.current?.focus({ preventScroll: true });
  }, [active]);
  return <section ref={panelRef} tabIndex={-1} hidden={!active} aria-label={`Compose: ${title}`}
    className={cn(active ? 'flex' : 'hidden', 'h-full min-h-0 flex-col overflow-hidden bg-bun-surface outline-none', !fullscreen && 'rounded-t-bun-xl border border-bun-line shadow-bun-modal')}>
    <header className="flex shrink-0 flex-wrap items-center gap-2 border-b border-bun-line px-3 py-3 sm:px-[22px] sm:py-[14px]">
      <BackButton labelHidden onClick={onMinimize}>Back to page</BackButton>
      <div className="min-w-0 flex-1"><h2 className="m-0 truncate font-instrument text-bun-heading sm:text-bun-metric font-semibold leading-snug tracking-normal">{title}</h2><p className="m-0 mt-0.5 text-bun-meta text-bun-stone">{status}</p></div>
      <Button variant="secondary" onClick={onToggleFullscreen} aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}><ArrowsExpandIcon className="h-3.5 w-3.5" /><span className="hidden sm:inline">{fullscreen ? 'Exit full screen' : 'Full screen'}</span></Button>
      <Button variant="secondary" onClick={onMinimize} aria-label="Minimize"><MinusIcon className="h-3.5 w-3.5" /><span className="hidden sm:inline">Minimize</span></Button>
      <Button variant="ghost" iconOnly onClick={onClose} aria-label="Discard draft"><XIcon className="h-4 w-4" /></Button>
    </header>
    {otherDrafts && <div className="flex shrink-0 items-center gap-2 overflow-x-auto border-b border-bun-line-soft bg-bun-canvas px-[22px] py-2"><span className="shrink-0 text-bun-label font-semibold uppercase tracking-wide text-bun-faint">Other drafts</span>{otherDrafts}</div>}
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">{children}</div>
    <footer className="flex shrink-0 flex-wrap items-center gap-2 border-t border-bun-line-soft bg-bun-canvas px-3 py-[10px] pb-[max(10px,env(safe-area-inset-bottom))]">{footer}</footer>
  </section>;
}

function Draft({ title, onOpen, onClose, minimized = false }) {
  return <div className={cn('flex shrink-0 items-center overflow-hidden', minimized ? 'w-[220px] rounded-t-bun-lg bg-bun-ink text-bun-on-ink shadow-bun-menu' : 'max-w-[240px] rounded-full border border-bun-line bg-bun-surface text-bun-slate')}>
    <button type="button" onClick={onOpen} className={cn('flex min-h-11 sm:min-h-0 min-w-0 flex-1 items-center gap-2 border-0 bg-transparent px-3 text-left font-instrument text-bun-body font-strong outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-bun-blue', minimized ? 'py-3' : 'py-1')}>
      {minimized && <PencilAltIcon className="h-3.5 w-3.5 shrink-0" />}<span className="truncate">{title}</span>
    </button>
    <button type="button" onClick={onClose} aria-label={`Discard ${title}`} className="flex min-h-11 min-w-11 sm:min-h-8 sm:min-w-8 items-center justify-center border-0 bg-transparent text-current opacity-60 hover:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-bun-blue"><XIcon className="h-3.5 w-3.5" /></button>
  </div>;
}

function Row({ label, htmlFor, actions, children }) {
  return <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-bun-line-soft px-[14px] py-2 sm:min-h-[56px]">
    <label htmlFor={htmlFor} className="min-w-[40px] shrink-0 text-bun-meta font-strong text-bun-stone">{label}</label>
    <div className="min-w-0 flex-1">{children}</div>{actions}
  </div>;
}

ComposeWorkspace.Panel = Panel;
ComposeWorkspace.Draft = Draft;
ComposeWorkspace.Row = Row;
